import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import * as React from 'react'
import { Button } from 'src/shared/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shared/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import { InFieldLabel, inFieldShellClass } from 'src/shared/components/ui/field'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

export const Combobox = ({
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
  searchPlaceholder,
  onSearchChange,
  isLoading = false,
  emptyMessage,
  clearOnReselect = false,
  buttonVariant,
  buttonClassName,
  wrapTrigger,
  ariaLabel,
  label,
}: {
  value: string
  onValueChange: (next: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
  disabled?: boolean
  searchPlaceholder?: string
  onSearchChange?: (search: string) => void
  isLoading?: boolean
  emptyMessage?: string
  clearOnReselect?: boolean
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
  buttonClassName?: string
  wrapTrigger?: (trigger: React.ReactElement) => React.ReactNode
  ariaLabel?: string
  label?: React.ReactNode
}) => {
  const { t } = useLocalization()
  const [open, setOpen] = React.useState(false)

  const defaultSearchPlaceholder = searchPlaceholder || t('Search...', 'Sök...')
  const defaultSelectPlaceholder = placeholder || t('Select...', 'Välj...')
  const noResultsText = t('No results found.', 'Inga resultat hittades.')
  const loadingText = t('Loading...', 'Laddar...')

  const selectedLabel =
    options.find((option) => option.value === value)?.label ||
    defaultSelectPlaceholder
  const floated = Boolean(label) && (open || Boolean(value))

  const trigger = label ? (
    <button
      type="button"
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        inFieldShellClass(),
        'flex h-14 w-full items-end justify-between px-4 pb-2 pt-5 text-left text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
        buttonClassName
      )}
    >
      <InFieldLabel floated={floated}>{label}</InFieldLabel>
      <span className={cn('min-w-0 flex-1 truncate', !floated && 'opacity-0')}>
        {value ? selectedLabel : defaultSelectPlaceholder}
      </span>
      <CaretSortIcon className="mb-0.5 ml-2 h-4 w-4 shrink-0 opacity-50" />
    </button>
  ) : (
    <Button
      variant={buttonVariant}
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel}
      className={cn('w-full justify-between', buttonClassName)}
      disabled={disabled}
    >
      {value ? selectedLabel : defaultSelectPlaceholder}
      <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {wrapTrigger ? wrapTrigger(trigger) : trigger}
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder={defaultSearchPlaceholder}
            className="h-9"
            onValueChange={onSearchChange}
          />

          <CommandList className="max-h-[250px]">
            <CommandEmpty>
              {isLoading ? loadingText : emptyMessage ?? noResultsText}
            </CommandEmpty>

            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    const next =
                      clearOnReselect && value === option.value
                        ? ''
                        : option.value
                    onValueChange(next)
                    setOpen(false)
                  }}
                >
                  {option.label}
                  <CheckIcon
                    className={cn(
                      'ml-auto h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
